import React, { useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Theme, makeStyles, createStyles } from "@material-ui/core/styles";
import { Checkbox, FormControlLabel } from "@material-ui/core";

import { addToLog } from "redux/LogBox/LogBox.actions";
import { submitForm } from "redux/FuelComposition/FuelComposition.actions";

import { prepareCalculationData, validateForm } from "utils/form.utils";
import { prepareDampers, damperValuesAreValid, prepareStartupBurnerConfig, validateStartupBurnerConfig } from "utils/dampers.utils";

import { DamperData, FuelCompositionState, StartupBurnerState, StoreState } from "models";
import { timeToUTCTimeStamp } from "utils/time.utils";

import CustomButton from "./CustomButton";

const localStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonContainer: {
      marginTop: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
    },
    gmtOffset: {
      fontSize: "13px",
      marginLeft: "9px",
    },
  }),
);

const Trigger = () => {
  const dispatch = useDispatch();
  const localClasses = localStyles();

  const startTimeStamp = useSelector((state: StoreState) => state.timeSelector.startTime);
  const endTimeStamp = useSelector((state: StoreState) => state.timeSelector.endTime);
  const clientId = useSelector((state: StoreState) => state.login.user?.clientId);

  const selectedMill = useSelector((state: StoreState) => state.millSelector.selectedMill);
  const fetchingMills = useSelector((state: StoreState) => state.millSelector.fetching);
  const selectedLine = useSelector((state: StoreState) => state.millSelector.selectedLineId);

  const form: FuelCompositionState = useSelector((state: StoreState) => state.fuelComposition);
  const submitting = useSelector((state: StoreState) => state.fuelComposition.submitPending);

  const dampers: DamperData[] = useSelector((state: StoreState) => state.dampers.data);
  const startupBurnerConfig: StartupBurnerState = useSelector((state: StoreState) => state.startupBurnerOverride.data);
  const gmtOffset: number = useSelector((state: StoreState) =>
    state.millSelector.selectedMill ? parseInt(state.millSelector.selectedMill.offset) : 0,
  );


  const [superHeater, setSuperHeater] = useState(false);

  const handleSubmit = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    if (!clientId && !selectedMill) {
      return console.error(`clientId: ${clientId} selectedMill: ${selectedMill}`);
    }

    if (!validateForm(form)) {
      dispatch(addToLog({ text: "Fuel composition or dates are invalid!", severity: "error" }));
      return console.error("Form or dates are invalid!");
    }

    const calculationData = prepareCalculationData(form);

    if (!damperValuesAreValid(dampers)) {
      dispatch(addToLog({ text: "Damper positions are invalid!", severity: "error" }));
      return console.error("Invalid damper positions!");
    }

    if (!validateStartupBurnerConfig(startupBurnerConfig.tags)) {
      dispatch(addToLog({ text: "Startup Burner positions are invalid!", severity: "error" }));
      return console.error("Invalid startup burner positions!");
    }

    const preparedDampers = prepareDampers(dampers);
    const preparedStartupBurnerConfig = prepareStartupBurnerConfig(startupBurnerConfig);

    calculationData.startupBurnerConfiguration = preparedStartupBurnerConfig;
    calculationData.damperConfigs = preparedDampers as DamperData[];
    calculationData.clientId = clientId as string;
    calculationData.millId = selectedMill!.millId;
    calculationData.lineId = selectedLine;
    calculationData.startTime = timeToUTCTimeStamp(new Date(startTimeStamp), gmtOffset).startTimeStamp;
    calculationData.endTime = timeToUTCTimeStamp(new Date(endTimeStamp), gmtOffset).startTimeStamp;
    calculationData.superHeater = superHeater;

    const lineIdIndex: number = selectedMill!.lineIds!.findIndex((line) => line === selectedLine);
    calculationData.segmentId = selectedMill!.segmentIds[lineIdIndex] || "";

    console.log(calculationData);
    dispatch(submitForm(calculationData, !calculationData.damperConfigs.length));
  };

  const toggleSuperHeater = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSuperHeater(event.target.checked);
  };

  return (
    <div className={localClasses.buttonContainer}>
      <FormControlLabel
        control={
          <Checkbox
            checked={superHeater}
            onChange={toggleSuperHeater}
            name="superheater"
            color="primary"
            disabled={submitting}
            inputProps={{ "aria-label": "Include superheater" }}
          />
        }
        labelPlacement="start"
        label="Include superheater"
      />
      <CustomButton
        disabled={submitting || fetchingMills || !selectedLine}
        text={submitting ? "Submitting form data" : fetchingMills ? "Loading mills" : "Trigger calculation"}
        handleClick={(event) => handleSubmit(event)}
      />
    </div>
  );
};

export default Trigger;
